import React, { useState, useRef, useEffect, useMemo } from 'react';
import './RightPanel.css';
import TransposeCapo from './TransposeCapo';
import ExportButtons from './ExportButtons';
import ChordSheetDisplay from './ChordSheetDisplay';
import PlaybackBar from './PlaybackBar';
import StrumPatternDisplay from './StrumPatternDisplay';
import { useAudioPlayback } from '../hooks/useAudioPlayback';

const TIME_SIG_OPTIONS = ['2/4', '3/4', '4/4', '6/8', '12/8'];

function RightPanel({
  lyrics,
  alignment,
  songKey,
  bpm,
  onBpmChange,
  beatsPerMeasure,
  onBeatsPerMeasureChange,
  transposition,
  capo,
  onTranspose,
  onCapoChange,
  simplified,
  onSimplify,
  highlightWordsDuringPlayback,
  onHighlightWordsDuringPlaybackChange,
  timingEditMode,
  onTimingEditModeChange,
  autoFollowPlayback,
  onAutoFollowPlaybackChange,
  detecting,
  onCancelDetect,
  error,
  title,
  onTitleChange,
  onRationalize,
  isRationalized,
  rationalizationCount,
  onAlignmentChange,
  audioObjectUrl,
  instrumentalAudioObjectUrl,
  vocalsAudioObjectUrl,
  rawChords,
  rawAnalysis,
  timedLyrics,
  onTimedLyricsChange,
  onSave,
  onOpenLibrary,
  hasSong,
  analysisProgress,
}) {
  const formatEta = (etaSec) => {
    const total = Number(etaSec);
    if (!Number.isFinite(total) || total < 0) return null;
    const secs = Math.floor(total);
    const minutes = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${String(minutes).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
  };

  const progressEta = analysisProgress ? formatEta(analysisProgress.etaSec) : null;
  // playMode: 'song' | 'instrumental' | 'vocals'
  const [playMode, setPlayMode] = useState('song');
  const playbackUrl =
    (playMode === 'instrumental' && instrumentalAudioObjectUrl) ? instrumentalAudioObjectUrl :
    (playMode === 'vocals' && vocalsAudioObjectUrl) ? vocalsAudioObjectUrl :
    audioObjectUrl;

  // Extend the alignment with synthetic carry-forward entries so useAudioPlayback's
  // activePosition advances through carry-forward chord positions during playback.
  // Without this, the activePosition stays at the last real chord while carry-forward
  // chords displayed on subsequent words are never individually highlighted via the hook.
  const playbackAlignment = useMemo(() => {
    const base = alignment || [];
    const words = timedLyrics || [];
    if (!base.length || !words.length) return base;
    const realKeys = new Set(base.map(e => `${e.lineIndex}:${e.wordIndex}`));
    const sorted = base.filter(e => e.time != null).sort((a, b) => a.time - b.time);
    const extras = [];
    for (let i = 0; i < sorted.length; i++) {
      const entry = sorted[i];
      const nextTime = sorted[i + 1]?.time ?? Infinity;
      for (const w of words) {
        if (w.start == null) continue;
        const wt = Number(w.start);
        // Only words in the span after this chord and before the next chord
        if (wt <= entry.time || wt >= nextTime) continue;
        const key = `${w.lineIndex}:${w.wordIndex}`;
        if (!realKeys.has(key)) {
          extras.push({ chord: entry.chord, lineIndex: w.lineIndex, wordIndex: w.wordIndex, time: wt });
        }
      }
    }
    return extras.length > 0 ? [...base, ...extras] : base;
  }, [alignment, timedLyrics]);

  const { playing, currentTime, duration, activePosition, toggle: togglePlayback, seek } = useAudioPlayback(playbackUrl, playbackAlignment);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [editingSig, setEditingSig] = useState(false);
  const [editingBpm, setEditingBpm] = useState(false);
  const [bpmDraft, setBpmDraft] = useState('');
  // Reset play mode to 'song' if the selected stem becomes unavailable
  useEffect(() => {
    if (playMode === 'instrumental' && !instrumentalAudioObjectUrl) setPlayMode('song');
    if (playMode === 'vocals' && !vocalsAudioObjectUrl) setPlayMode('song');
  }, [instrumentalAudioObjectUrl, vocalsAudioObjectUrl, playMode]);
  const contentRef = useRef(null);

  // Scroll content area to top when detection starts
  useEffect(() => {
    if (detecting && contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [detecting]);

  // Derive display label — 6/8 and 12/8 are common compound meters
  const timeSigLabel = beatsPerMeasure
    ? (beatsPerMeasure === 6 ? '6/8' : beatsPerMeasure === 12 ? '12/8' : `${beatsPerMeasure}/4`)
    : null;

  // Strum pattern from analysis (only present in stem-separated analyses)
  const strumPattern = rawAnalysis?.strumPattern ?? null;

  return (
    <section className="right-panel">
      {/* Sticky control bar */}
      <div className="right-panel__toolbar">
        <div className="right-panel__toolbar-row right-panel__toolbar-row--top">
          <div className="right-panel__toolbar-left">
          {/* Editable song title */}
          <input
            className="song-title-input"
            type="text"
            value={title && title !== 'Untitled Song' ? title : ''}
            onChange={e => onTitleChange && onTitleChange(e.target.value)}
            placeholder="Song title…"
            aria-label="Song title"
          />
          {songKey && (
            <span className="key-badge" title="Detected key">
              Key: <strong>{songKey}</strong>
            </span>
          )}
          {bpm && (
            editingBpm ? (
              <input
                className="sig-select"
                style={{ width: 90 }}
                autoFocus
                type="number"
                min="20"
                max="320"
                step="1"
                value={bpmDraft}
                onChange={(e) => setBpmDraft(e.target.value)}
                onBlur={() => {
                  const n = Number(bpmDraft);
                  if (Number.isFinite(n) && n > 0 && onBpmChange) onBpmChange(Math.round(n));
                  setEditingBpm(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const n = Number(bpmDraft);
                    if (Number.isFinite(n) && n > 0 && onBpmChange) onBpmChange(Math.round(n));
                    setEditingBpm(false);
                  } else if (e.key === 'Escape') {
                    setEditingBpm(false);
                  }
                }}
              />
            ) : (
              <span
                className="key-badge key-badge--clickable"
                title="BPM — click to edit"
                onClick={() => {
                  setBpmDraft(String(Math.round(Number(bpm))));
                  setEditingBpm(true);
                }}
              >
                BPM: <strong>{bpm}</strong>
              </span>
            )
          )}
          {timeSigLabel && !editingSig && (
            <span
              className="key-badge key-badge--clickable"
              title="Time signature — click to change"
              onClick={() => setEditingSig(true)}
            >
              {timeSigLabel}
            </span>
          )}
          {editingSig && (
            <select
              className="sig-select"
              autoFocus
              value={timeSigLabel || '4/4'}
              onChange={(e) => {
                const num = parseInt(e.target.value.split('/')[0], 10);
                onBeatsPerMeasureChange(num);
                setEditingSig(false);
              }}
              onBlur={() => setEditingSig(false)}
            >
              {TIME_SIG_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {alignment && alignment.length > 0 && (
            <button
              className={`simplify-btn${simplified ? ' simplify-btn--active' : ''}`}
              onClick={() => onSimplify(!simplified)}
              title="Strip chord extensions down to basic triads (e.g. Gmaj7 → G, Dm7 → Dm)"
            >
              Simplify
            </button>
          )}
          {alignment && alignment.length > 0 && (
            <button
              className={`simplify-btn${isRationalized ? ' simplify-btn--active' : ''}`}
              onClick={onRationalize}
              title="Analyze song structure: normalize chords across like sections (verses, choruses) using consensus logic to fix likely misdetections"
            >
              {isRationalized
                ? rationalizationCount > 0
                  ? `Rationalized (${rationalizationCount})`
                  : 'Rationalized ✓'
                : 'Rationalize'}
            </button>
          )}
          {alignment && alignment.length > 0 && songKey && (
            <button
              className={`simplify-btn${showAnalysis ? ' simplify-btn--active' : ''}`}
              onClick={() => setShowAnalysis(v => !v)}
              title="Show Roman numeral analysis (I, ii, V, etc.) below each chord"
            >
              Analysis
            </button>
          )}
          </div>
          <div className="right-panel__toolbar-right">
          {rawAnalysis && (
            <>
              <StrumPatternDisplay
                strumPattern={strumPattern}
                beatsPerMeasure={beatsPerMeasure}
              />
              <div className="toolbar-divider" />
            </>
          )}
          <button
            className="simplify-btn"
            onClick={onOpenLibrary}
            title="Open song library"
          >
            📚
          </button>
          {hasSong && (
            <button
              className="simplify-btn"
              onClick={onSave}
              title="Save current song to library"
            >
              💾 Save
            </button>
          )}
          <div className="toolbar-divider" />
          <TransposeCapo
            transposition={transposition}
            capo={capo}
            onTranspose={onTranspose}
            onCapoChange={onCapoChange}
            songKey={songKey}
          />
          <div className="toolbar-divider" />
          <ExportButtons
            title={title}
            songKey={songKey}
            capo={capo}
            lyrics={lyrics}
            alignment={alignment}
            timedLyrics={timedLyrics}
            transposition={transposition}
            simplified={simplified}
            bpm={bpm}
            strumPattern={strumPattern}
          />
          </div>
        </div>
        <div className="right-panel__toolbar-row right-panel__toolbar-row--playback">
          {audioObjectUrl && (
            <PlaybackBar
              playing={playing}
              currentTime={currentTime}
              duration={duration}
              onToggle={togglePlayback}
              onSeek={seek}
            />
          )}
          {alignment && alignment.length > 0 && (
            <label className="key-badge key-badge--clickable" title="Bold the currently sung word during playback">
              <input
                type="checkbox"
                checked={!!highlightWordsDuringPlayback}
                onChange={(e) => onHighlightWordsDuringPlaybackChange && onHighlightWordsDuringPlaybackChange(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Word Highlight
            </label>
          )}
          {alignment && alignment.length > 0 && (
            <label className="key-badge key-badge--clickable" title="Enable drag handles to edit word timing boundaries">
              <input
                type="checkbox"
                checked={!!timingEditMode}
                onChange={(e) => onTimingEditModeChange && onTimingEditModeChange(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Timing Edit
            </label>
          )}
          {alignment && alignment.length > 0 && (
            <label className="key-badge key-badge--clickable" title="Auto-scroll playback to keep active line centered">
              <input
                type="checkbox"
                checked={!!autoFollowPlayback}
                onChange={(e) => onAutoFollowPlaybackChange && onAutoFollowPlaybackChange(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Auto Follow
            </label>
          )}
          {(instrumentalAudioObjectUrl || vocalsAudioObjectUrl) && (
            <label className="key-badge key-badge--clickable" title="Switch audio track" style={{ gap: 6 }}>
              Play:
              <select
                value={playMode}
                onChange={(e) => setPlayMode(e.target.value)}
                className="play-mode-select"
              >
                <option value="song">Song</option>
                {instrumentalAudioObjectUrl && <option value="instrumental">Instrumental</option>}
                {vocalsAudioObjectUrl && <option value="vocals">Vocals</option>}
              </select>
            </label>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="right-panel__content" ref={contentRef}>
        {detecting && (
          <div className="right-panel__overlay">
            <div className="right-panel__spinner" />
            {analysisProgress ? (
              <div className="right-panel__progress">
                <p className="right-panel__progress-step">{analysisProgress.step}</p>
                <div className="right-panel__progress-bar-track">
                  <div
                    className="right-panel__progress-bar-fill"
                    style={{ width: `${Math.max(2, analysisProgress.pct)}%` }}
                  />
                </div>
                <p className="right-panel__progress-pct">
                  {analysisProgress.pct}%
                  {progressEta ? ` • ETA ${progressEta}` : ''}
                </p>
              </div>
            ) : (
              <p>Analyzing audio…</p>
            )}
            <button className="cancel-detect-btn" onClick={onCancelDetect}>
              Cancel
            </button>
          </div>
        )}

        {error && !detecting && (
          <div className="error-banner">
            <span className="error-banner__icon">⚠️</span>
            {error}
          </div>
        )}

        <ChordSheetDisplay
          lyrics={lyrics}
          alignment={alignment}
          bpm={bpm}
          transposition={transposition}
          capo={capo}
          songKey={songKey}
          simplified={simplified}
          onAlignmentChange={onAlignmentChange}
          activePosition={activePosition}
          currentTime={currentTime}
          showAnalysis={showAnalysis}
          rawAnalysis={rawAnalysis}
          highlightWordsDuringPlayback={highlightWordsDuringPlayback}
          timingEditMode={timingEditMode}
          autoFollowPlayback={autoFollowPlayback}
          timedLyrics={timedLyrics}
          onTimedLyricsChange={onTimedLyricsChange}
        />
      </div>
    </section>
  );
}

export default RightPanel;
